import { BrowserQRCodeReader, IScannerControls } from "@zxing/browser";
import { DecodeHintType } from "@zxing/library";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";

export type ScannerZxingProps = {
	onResult: (result: { data: string }) => void;
	className?: string;
};

export type ScannerZxingRef = {
	switchCamera: () => Promise<void>;
	hasMultipleCameras: () => Promise<boolean>;
	toggleTorch: () => Promise<void>;
};

export const ScannerZxing = forwardRef<ScannerZxingRef, ScannerZxingProps>((props, forwardedRef) => {
	const { onResult, className } = props;
	const onResultRef = useRef(onResult);
	const controlsRef = useRef<IScannerControls | null>(null);
	const readerRef = useRef<BrowserQRCodeReader | null>(null);
	const videoRef = useRef<HTMLVideoElement | null>(null);
	const currentCameraIndexRef = useRef(0);
	const camerasRef = useRef<MediaDeviceInfo[]>([]);
	const torchEnabledRef = useRef(false);

	onResultRef.current = onResult;

	useImperativeHandle(forwardedRef, () => ({
		switchCamera: async () => {
			if (!readerRef.current || !videoRef.current) return;

			const cameras = await BrowserQRCodeReader.listVideoInputDevices();
			if (cameras.length <= 1) return;

			camerasRef.current = cameras;

			// Stop current scanning
			if (controlsRef.current) {
				controlsRef.current.stop();
				controlsRef.current = null;
			}

			// Switch to next camera
			currentCameraIndexRef.current = (currentCameraIndexRef.current + 1) % cameras.length;
			const nextCamera = cameras[currentCameraIndexRef.current];
			if (!nextCamera) return;

			// Start scanning with new camera
			const controls = await readerRef.current.decodeFromVideoDevice(nextCamera.deviceId, videoRef.current, (result) => {
				if (result) {
					onResultRef.current({ data: result.getText() });
				}
			});

			controlsRef.current = controls;
		},
		hasMultipleCameras: async () => {
			const cameras = await BrowserQRCodeReader.listVideoInputDevices();
			return cameras.length > 1;
		},
		toggleTorch: async () => {
			if (!videoRef.current) return;

			const stream = videoRef.current.srcObject as MediaStream | null;
			if (!stream) return;

			const track = stream.getVideoTracks()[0];
			if (!track) return;

			const capabilities = track.getCapabilities() as MediaTrackCapabilities & { torch?: boolean };
			if (!capabilities.torch) return;

			torchEnabledRef.current = !torchEnabledRef.current;

			await track.applyConstraints({
				advanced: [{ torch: torchEnabledRef.current } as MediaTrackConstraintSet],
			});
		},
	}));

	const ref = useCallback((el: HTMLVideoElement | null) => {
		videoRef.current = el;

		if (!el) {
			if (controlsRef.current) {
				controlsRef.current.stop();
				controlsRef.current = null;
			}
			readerRef.current = null;
			return;
		}

		// Create hints for better detection
		const hints = new Map<DecodeHintType, unknown>();
		hints.set(DecodeHintType.TRY_HARDER, true);

		const reader = new BrowserQRCodeReader(hints);
		readerRef.current = reader;

		// Start scanning with default camera (undefined = first available)
		reader
			.decodeFromVideoDevice(undefined, el, (result) => {
				if (result) {
					onResultRef.current({ data: result.getText() });
				}
			})
			.then((controls) => {
				controlsRef.current = controls;
			})
			.catch((error) => {
				console.error("Failed to start QR scanner:", error);
			});
	}, []);

	return <video ref={ref} className={className} />;
});
